import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { TextInput } from '../../../shared/components/TextInput';
import {
  consultarAcompanhamento,
  solicitarCodigoAcesso,
  type TrackedApplication,
} from '../api';

const STATUS_LABELS: Record<string, string> = {
  em_analise: 'Em análise',
  entrevista_agendada: 'Entrevista agendada',
  aprovado: 'Aprovado',
  reprovado: 'Encerrado',
  banco_talentos: 'No banco de talentos',
};

/**
 * Acompanhamento do próprio processo.
 *
 * O candidato informa o e-mail, recebe um código de uso único e consulta
 * o andamento — sem conta, sem senha. A resposta ao pedido de código é
 * sempre a mesma, exista o e-mail ou não: a tela reflete isso dizendo
 * "se houver candidatura, o código foi enviado".
 */
export default function AcompanharPage(): React.ReactElement {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [codigoSolicitado, setCodigoSolicitado] = useState(false);
  const [candidaturas, setCandidaturas] = useState<TrackedApplication[] | null>(null);

  const solicitar = useMutation({
    mutationFn: () => solicitarCodigoAcesso(email.trim()),
    onSuccess: () => setCodigoSolicitado(true),
  });

  const consultar = useMutation({
    mutationFn: () => consultarAcompanhamento(email.trim(), token.trim()),
    onSuccess: (result) => setCandidaturas(result.applications),
  });

  function mensagemErro(error: unknown): string {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      if (status === 401) return 'Código inválido, expirado ou já utilizado. Solicite um novo.';
      if (status === 403) return 'Este código não pertence ao e-mail informado.';
      if (status === 429) return 'Muitas tentativas. Aguarde alguns minutos.';
    }
    return 'Não foi possível consultar agora. Tente novamente.';
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="mx-auto max-w-2xl px-4 py-8">
        <Link to="/vagas" className="mb-4 inline-flex items-center gap-1 text-sm text-blue-600">
          <ArrowLeft className="h-4 w-4" /> Vagas abertas
        </Link>

        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h1 className="text-lg font-semibold text-gray-900">Acompanhar candidatura</h1>
          <p className="mt-1 text-sm text-gray-600">
            Informe o e-mail usado na inscrição. Enviaremos um código de acesso válido por poucos
            minutos.
          </p>

          <div className="mt-4">
            <TextInput
              label="E-mail"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>

          <Button
            variant="secondary"
            className="mt-3"
            disabled={!email.trim()}
            loading={solicitar.isPending}
            onClick={() => solicitar.mutate()}
          >
            Enviar código
          </Button>

          {codigoSolicitado && (
            <Alert variant="info" className="mt-3">
              Se houver candidatura para este e-mail, o código de acesso foi enviado.
            </Alert>
          )}

          <div className="mt-4">
            <TextInput
              label="Código de acesso"
              inputMode="numeric"
              value={token}
              onChange={(event) => setToken(event.target.value)}
            />
          </div>

          <Button
            className="mt-3 w-full"
            disabled={!email.trim() || !token.trim()}
            loading={consultar.isPending}
            onClick={() => consultar.mutate()}
          >
            Consultar andamento
          </Button>

          {consultar.isError && (
            <Alert variant="error" className="mt-3">
              {mensagemErro(consultar.error)}
            </Alert>
          )}
        </div>

        {candidaturas && (
          <section className="mt-6 space-y-3">
            {candidaturas.length === 0 ? (
              <EmptyState
                title="Nenhuma candidatura encontrada"
                description="Não localizamos candidaturas para este e-mail."
              />
            ) : (
              candidaturas.map((candidatura) => (
                <article
                  key={candidatura.protocol}
                  className="rounded-lg border border-gray-200 bg-white p-4"
                >
                  <h2 className="text-sm font-semibold text-gray-900">
                    {candidatura.jobTitle ?? 'Vaga'}
                  </h2>
                  <p className="mt-1 text-xs text-gray-500">
                    Protocolo {candidatura.protocol} · inscrito em{' '}
                    {new Date(candidatura.appliedAt).toLocaleDateString('pt-BR')}
                  </p>
                  <p className="mt-2 text-sm text-gray-700">
                    Etapa atual: <strong>{candidatura.stageLabel}</strong> —{' '}
                    {STATUS_LABELS[candidatura.status] ?? candidatura.status}
                  </p>

                  {candidatura.upcomingSchedules.length > 0 && (
                    <ul className="mt-2 space-y-1 text-sm text-gray-700">
                      {candidatura.upcomingSchedules.map((agendamento) => (
                        <li key={agendamento.scheduledAt}>
                          Próximo encontro:{' '}
                          {new Date(agendamento.scheduledAt).toLocaleString('pt-BR', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                          {agendamento.location ? ` — ${agendamento.location}` : ''}
                          {agendamento.meetingLink ? (
                            <>
                              {' — '}
                              <a
                                href={agendamento.meetingLink}
                                className="text-blue-600 hover:underline"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                link da reunião
                              </a>
                            </>
                          ) : (
                            ''
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              ))
            )}
          </section>
        )}
      </main>
    </div>
  );
}
